import React, { useState, useMemo } from 'react';
import {
  Calendar, Clock, CheckCircle, AlertTriangle, TrendingUp, Star,
  GraduationCap, PenTool, BarChart3, Target, Zap, ArrowRight,
  ChevronDown, ChevronRight, Play, Users, Bell, Settings, Menu
} from 'lucide-react';

/**
 * CollegeJourneyHub - Contextual Reorganization Mockup
 *
 * Key Design Principles:
 * 1. CONTEXTUAL INTELLIGENCE - Layout adapts based on user context (deadlines, phase, capacity)
 * 2. COLLEGE CARDS PROMINENCE - College cards as exciting, motivating centerpiece
 * 3. UNIFIED TASK MANAGEMENT - Consistent with UnifiedTimelineIntegrated patterns
 * 4. CLEAR VISUAL HIERARCHY - One primary focus area, progressive disclosure for secondary
 * 5. DESKTOP-FIRST - Leverage desktop strengths for complex layouts
 * 6. SEMANTIC COLOR USAGE - teal primary, coral urgent, gold success, azure info, indigo focus
 * 7. STRATEGIC slate-900 BORDERS - Visual weight and hierarchy
 */

// Context Analysis Engine - Determines primary focus based on user situation
const useContextualIntelligence = (colleges, deadlines, timeline, userContext) => {
  return useMemo(() => {
    const today = new Date();
    const oneWeek = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
    const twoWeeks = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);

    // Analyze urgent deadlines
    const urgentDeadlines = deadlines?.filter(d => new Date(d.date) <= oneWeek) || [];
    const approachingDeadlines = deadlines?.filter(d => {
      const date = new Date(d.date);
      return date > oneWeek && date <= twoWeeks;
    }) || [];

    // Analyze current phase
    const totalColleges = colleges?.length || 0;
    const hasAssessment = userContext?.assessmentComplete || false;
    const essayCount = userContext?.essayCount || 0;

    let currentPhase = 'discovery';
    if (totalColleges >= 8) currentPhase = 'application';
    else if (totalColleges >= 4 || essayCount > 0) currentPhase = 'development';
    else if (hasAssessment) currentPhase = 'research';

    // Analyze workload capacity
    const thisWeekHours = timeline?.thisWeekHours || 0;
    let capacity = 'balanced';
    if (thisWeekHours < 8) capacity = 'light';
    else if (thisWeekHours > 25) capacity = 'heavy';

    // Determine primary context
    let primaryContext = 'progress';
    if (urgentDeadlines.length > 0) primaryContext = 'deadline';
    else if (capacity === 'heavy') primaryContext = 'capacity';
    else if (currentPhase === 'discovery') primaryContext = 'phase';

    return {
      primaryContext,
      currentPhase,
      capacity,
      urgentDeadlines,
      approachingDeadlines,
      insights: {
        needsAssessment: !hasAssessment,
        needsMoreColleges: totalColleges < 4,
        needsEssayWork: essayCount < 3,
        upcomingDeadlinePressure: approachingDeadlines.length > 2
      }
    };
  }, [colleges, deadlines, timeline, userContext]);
};

// Primary Focus Card - Adapts based on context
const ContextualPrimaryFocus = ({ context, colleges, deadlines, onAction }) => {
  const getFocusContent = () => {
    switch (context.primaryContext) {
      case 'deadline':
        return {
          title: 'Urgent Deadlines',
          subtitle: `${context.urgentDeadlines.length} deadline${context.urgentDeadlines.length > 1 ? 's' : ''} this week`,
          icon: <Bell className="w-6 h-6 text-coral-600" />,
          bgColor: 'bg-coral-50',
          borderColor: 'border-coral-400',
          textColor: 'text-coral-900',
          actionText: 'View Deadlines',
          actionColor: 'bg-coral-600 hover:bg-coral-700',
          items: context.urgentDeadlines.slice(0, 3)
        };

      case 'capacity':
        return {
          title: 'Workload Management',
          subtitle: 'Heavy week - let\'s prioritize effectively',
          icon: <BarChart3 className="w-6 h-6 text-indigo-600" />,
          bgColor: 'bg-indigo-50',
          borderColor: 'border-indigo-400',
          textColor: 'text-indigo-900',
          actionText: 'Optimize Schedule',
          actionColor: 'bg-indigo-600 hover:bg-indigo-700',
          items: []
        };

      case 'phase':
        const phaseContent = {
          discovery: {
            title: 'Strategic Foundation',
            subtitle: 'Complete assessment to unlock personalized guidance',
            items: ['Strategic Assessment', 'Academic Profile', 'College Preferences']
          },
          research: {
            title: 'College Research',
            subtitle: 'Build your balanced college list',
            items: ['Find Reach Schools', 'Identify Target Schools', 'Select Safety Schools']
          },
          development: {
            title: 'Essay Development',
            subtitle: 'Craft compelling application essays',
            items: ['Personal Statement', 'Supplemental Essays', 'Activity Descriptions']
          },
          application: {
            title: 'Application Execution',
            subtitle: 'Submit strong, complete applications',
            items: ['Common App', 'Transcripts', 'Letters of Recommendation']
          }
        };

        const phase = phaseContent[context.currentPhase];
        return {
          title: phase.title,
          subtitle: phase.subtitle,
          icon: <Target className="w-6 h-6 text-teal-600" />,
          bgColor: 'bg-teal-50',
          borderColor: 'border-teal-400',
          textColor: 'text-teal-900',
          actionText: 'Get Started',
          actionColor: 'bg-teal-600 hover:bg-teal-700',
          items: phase.items
        };

      default:
        return {
          title: 'Progress Overview',
          subtitle: 'Your college journey is on track',
          icon: <CheckCircle className="w-6 h-6 text-emerald-600" />,
          bgColor: 'bg-emerald-50',
          borderColor: 'border-emerald-400',
          textColor: 'text-emerald-900',
          actionText: 'Continue',
          actionColor: 'bg-emerald-600 hover:bg-emerald-700',
          items: []
        };
    }
  };

  const content = getFocusContent();

  return (
    <div className={`${content.bgColor} border-2 ${content.borderColor} rounded-xl p-6`}>
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          {content.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className={`text-xl font-bold ${content.textColor} mb-1`}>
                {content.title}
              </h2>
              <p className={`text-sm ${content.textColor} opacity-80`}>
                {content.subtitle}
              </p>
            </div>
            <button
              onClick={() => onAction(context.primaryContext)}
              className={`px-4 py-2 ${content.actionColor} text-white rounded-lg text-sm font-medium transition-colors`}
            >
              {content.actionText}
            </button>
          </div>

          {/* Context-specific content */}
          {content.items.length > 0 && (
            <div className="space-y-2">
              {content.items.map((item, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-white bg-opacity-60 rounded-lg">
                  <div className="w-2 h-2 bg-current rounded-full opacity-60"></div>
                  <span className={`text-sm font-medium ${content.textColor}`}>
                    {typeof item === 'string' ? item : item.title}
                  </span>
                  {typeof item === 'object' && item.daysLeft && (
                    <span className="text-xs bg-white px-2 py-1 rounded-full ml-auto">
                      {item.daysLeft} days
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// College Cards Section - Always prominent, contextually organized
const CollegeShowcase = ({ colleges, context, onCollegeAction }) => {
  const [expandedCategory, setExpandedCategory] = useState('target');

  const collegesByCategory = useMemo(() => {
    const groups = {
      reach: colleges?.filter(c => c.category === 'reach') || [],
      target: colleges?.filter(c => c.category === 'target') || [],
      safety: colleges?.filter(c => c.category === 'safety') || []
    };

    // Add placeholders for visual consistency
    const targetCounts = { reach: 3, target: 4, safety: 3 };
    Object.entries(groups).forEach(([category, existing]) => {
      const needed = Math.max(0, targetCounts[category] - existing.length);
      for (let i = 0; i < needed; i++) {
        existing.push({
          id: `placeholder-${category}-${i}`,
          name: `Add ${category} school`,
          isPlaceholder: true,
          category
        });
      }
    });

    return groups;
  }, [colleges]);

  const getCategoryInfo = (category) => {
    const info = {
      reach: {
        name: 'Reach Schools',
        description: 'Reach high - these are your ambitious choices',
        color: 'from-violet-500 to-pink-500',
        bgColor: 'bg-violet-50',
        borderColor: 'border-violet-200',
        textColor: 'text-violet-900'
      },
      target: {
        name: 'Target Schools',
        description: 'Great fit schools where you\'ll thrive',
        color: 'from-azure-500 to-teal-500',
        bgColor: 'bg-azure-50',
        borderColor: 'border-azure-200',
        textColor: 'text-azure-900'
      },
      safety: {
        name: 'Safety Schools',
        description: 'Solid options that ensure your success',
        color: 'from-emerald-500 to-emerald-500',
        bgColor: 'bg-emerald-50',
        borderColor: 'border-emerald-200',
        textColor: 'text-emerald-900'
      }
    };
    return info[category];
  };

  const CollegeCard = ({ college }) => {
    if (college.isPlaceholder) {
      return (
        <button
          onClick={() => onCollegeAction('add', college.category)}
          className={`
            aspect-[4/3] rounded-lg border-2 border-dashed border-stone-300
            bg-stone-50 hover:bg-stone-100 transition-all duration-200
            flex flex-col items-center justify-center gap-2 text-stone-600
            hover:border-stone-400 hover:shadow-sm group
          `}
        >
          <div className="w-8 h-8 rounded-full bg-stone-200 group-hover:bg-stone-300 flex items-center justify-center transition-colors">
            <span className="text-lg">+</span>
          </div>
          <span className="text-sm font-medium text-center px-2">
            {college.name}
          </span>
        </button>
      );
    }

    const categoryInfo = getCategoryInfo(college.category);

    return (
      <div className="aspect-[4/3] rounded-lg bg-white border border-slate-900 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden group">
        {/* College header with gradient */}
        <div className={`h-16 bg-gradient-to-r ${categoryInfo.color} relative`}>
          <div className="absolute inset-0 bg-black bg-opacity-20"></div>
          <div className="relative p-3 flex items-center justify-between">
            <div className="w-8 h-8 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
              <GraduationCap className="w-4 h-4 text-stone-700" />
            </div>
            <button className="w-6 h-6 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center transition-colors">
              <Settings className="w-3 h-3 text-white" />
            </button>
          </div>
        </div>

        {/* College content */}
        <div className="p-4 flex-1 flex flex-col">
          <h3 className="font-bold text-slate-900 text-sm mb-1 line-clamp-2">
            {college.name}
          </h3>
          <p className="text-xs text-slate-600 mb-3">
            {college.location || 'Location TBD'}
          </p>

          {/* Progress indicators */}
          <div className="mt-auto space-y-2">
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1 bg-stone-200 rounded-full overflow-hidden">
                <div
                  className={`h-full bg-gradient-to-r ${categoryInfo.color} transition-all duration-300`}
                  style={{ width: `${college.applicationProgress || 15}%` }}
                />
              </div>
              <span className="text-xs text-slate-600">
                {college.applicationProgress || 15}%
              </span>
            </div>

            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-600">
                {college.essayCount || 0} essays
              </span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                college.applicationStatus === 'submitted' ? 'bg-emerald-100 text-emerald-800' :
                college.applicationStatus === 'in_progress' ? 'bg-gold-100 text-gold-800' :
                'bg-stone-100 text-stone-800'
              }`}>
                {college.applicationStatus || 'Not Started'}
              </span>
            </div>
          </div>
        </div>

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-5 transition-all duration-200 pointer-events-none">
          <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <ArrowRight className="w-4 h-4 text-slate-700" />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl border border-slate-900 shadow-sm">
      <div className="p-6 border-b border-stone-100">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xl font-bold text-slate-900">College Portfolio</h2>
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">
              {colleges?.length || 0} schools
            </span>
            <button className="px-3 py-1 bg-teal-100 hover:bg-teal-200 text-teal-800 rounded-lg text-sm font-medium transition-colors">
              Manage List
            </button>
          </div>
        </div>
        <p className="text-sm text-slate-600">
          Build a balanced portfolio that maximizes your opportunities
        </p>
      </div>

      <div className="p-6 space-y-6">
        {Object.entries(collegesByCategory).map(([category, categoryColleges]) => {
          const info = getCategoryInfo(category);
          const isExpanded = expandedCategory === category;

          return (
            <div key={category} className={`rounded-lg border ${info.borderColor} ${info.bgColor} overflow-hidden`}>
              <button
                onClick={() => setExpandedCategory(isExpanded ? null : category)}
                className="w-full p-4 flex items-center justify-between hover:bg-white hover:bg-opacity-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${info.color}`}></div>
                  <div className="text-left">
                    <h3 className={`font-bold ${info.textColor}`}>
                      {info.name} ({categoryColleges.filter(c => !c.isPlaceholder).length})
                    </h3>
                    <p className={`text-sm ${info.textColor} opacity-80`}>
                      {info.description}
                    </p>
                  </div>
                </div>
                <ChevronDown className={`w-5 h-5 ${info.textColor} transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
              </button>

              {isExpanded && (
                <div className="p-4 pt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {categoryColleges.map((college, index) => (
                      <CollegeCard key={college.id || index} college={college} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Secondary Focus Areas - Progressive Disclosure
const SecondaryFocusArea = ({ title, children, defaultExpanded = false }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className="bg-white rounded-xl border border-stone-200 shadow-sm">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-stone-50 transition-colors"
      >
        <h3 className="font-semibold text-slate-900">{title}</h3>
        <ChevronRight className={`w-5 h-5 text-slate-600 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-stone-100">
          <div className="pt-4">
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

// Timeline Integration Component
const TimelineIntegration = ({ tasks, onTaskAction }) => {
  const upcomingTasks = tasks?.slice(0, 4) || [];

  return (
    <div className="space-y-3">
      {upcomingTasks.map((task, index) => (
        <div key={task.id || index} className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors">
          <div className="w-2 h-2 bg-teal-500 rounded-full"></div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium text-slate-900 truncate">
              {task.title}
            </h4>
            <p className="text-xs text-slate-600">
              {task.college} • {task.timeEstimate}
            </p>
          </div>
          <button
            onClick={() => onTaskAction(task)}
            className="px-3 py-1 bg-teal-100 hover:bg-teal-200 text-teal-800 rounded-lg text-xs font-medium transition-colors"
          >
            Start
          </button>
        </div>
      ))}

      <button className="w-full text-center text-sm text-teal-600 hover:text-teal-800 font-medium py-2">
        View Full Timeline →
      </button>
    </div>
  );
};

// Main Component
const CollegeJourneyHubMockup = () => {
  // Mock data for demonstration
  const mockColleges = [
    {
      id: '1',
      name: 'Stanford University',
      location: 'Stanford, CA',
      category: 'reach',
      applicationProgress: 45,
      applicationStatus: 'in_progress',
      essayCount: 3
    },
    {
      id: '2',
      name: 'UC Berkeley',
      location: 'Berkeley, CA',
      category: 'target',
      applicationProgress: 70,
      applicationStatus: 'in_progress',
      essayCount: 2
    },
    {
      id: '3',
      name: 'Cal Poly SLO',
      location: 'San Luis Obispo, CA',
      category: 'safety',
      applicationProgress: 90,
      applicationStatus: 'submitted',
      essayCount: 1
    }
  ];

  const mockDeadlines = [
    { id: '1', title: 'Stanford Early Action', date: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000), college: 'Stanford' },
    { id: '2', title: 'UC Application', date: new Date(Date.now() + 12 * 24 * 60 * 60 * 1000), college: 'UC System' }
  ];

  const mockTimeline = {
    thisWeekHours: 15,
    tasks: [
      { id: '1', title: 'Complete Common App Essay', college: 'Multiple', timeEstimate: '2h' },
      { id: '2', title: 'Request Letter of Rec', college: 'Stanford', timeEstimate: '30m' },
      { id: '3', title: 'Research Merit Scholarships', college: 'UC Berkeley', timeEstimate: '1h' },
      { id: '4', title: 'Schedule Campus Visit', college: 'Cal Poly', timeEstimate: '45m' }
    ]
  };

  const mockUserContext = {
    assessmentComplete: true,
    essayCount: 2,
    profileComplete: 85
  };

  const contextualIntelligence = useContextualIntelligence(
    mockColleges,
    mockDeadlines,
    mockTimeline,
    mockUserContext
  );

  const handlePrimaryAction = (actionType) => {
    console.log('Primary action triggered:', actionType);
  };

  const handleCollegeAction = (action, college) => {
    console.log('College action:', action, college);
  };

  const handleTaskAction = (task) => {
    console.log('Task action:', task);
  };

  return (
    <div className="min-h-screen bg-stone-50" style={{ fontFamily: 'Reddit Sans, system-ui, sans-serif' }}>
      {/* Header */}
      <div className="bg-slate-900 text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <h1 className="text-2xl font-bold">College Journey</h1>
              <div className="flex items-center gap-4 text-sm">
                <span className="px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full">
                  {contextualIntelligence.currentPhase} Phase
                </span>
                <span className="text-stone-300">
                  Week capacity: {contextualIntelligence.capacity}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <button className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors">
                Timeline
              </button>
              <button className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
          {/* Main Content - Primary Focus + College Cards */}
          <div className="xl:col-span-3 space-y-8">
            {/* Primary Focus - Contextually Determined */}
            <ContextualPrimaryFocus
              context={contextualIntelligence}
              colleges={mockColleges}
              deadlines={mockDeadlines}
              onAction={handlePrimaryAction}
            />

            {/* College Cards - Always Prominent */}
            <CollegeShowcase
              colleges={mockColleges}
              context={contextualIntelligence}
              onCollegeAction={handleCollegeAction}
            />

            {/* Secondary Focus Areas - Progressive Disclosure */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <SecondaryFocusArea title="Essay Pipeline" defaultExpanded={contextualIntelligence.currentPhase === 'development'}>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Personal Statement</span>
                    <span className="text-xs bg-gold-100 text-gold-800 px-2 py-1 rounded-full">In Progress</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Why Stanford Essay</span>
                    <span className="text-xs bg-stone-100 text-stone-800 px-2 py-1 rounded-full">Not Started</span>
                  </div>
                  <button className="w-full text-center text-sm text-teal-600 hover:text-teal-800 font-medium py-2">
                    View Essay Hub →
                  </button>
                </div>
              </SecondaryFocusArea>

              <SecondaryFocusArea title="Application Status">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Common Application</span>
                    <span className="text-xs bg-gold-100 text-gold-800 px-2 py-1 rounded-full">75% Complete</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Transcripts Requested</span>
                    <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full">Complete</span>
                  </div>
                  <button className="w-full text-center text-sm text-teal-600 hover:text-teal-800 font-medium py-2">
                    View All Applications →
                  </button>
                </div>
              </SecondaryFocusArea>
            </div>
          </div>

          {/* Sidebar - Context-Aware Timeline Integration */}
          <div className="xl:col-span-1 space-y-6">
            {/* Smart Next Actions */}
            <div className="bg-white rounded-xl border border-slate-900 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Zap className="w-5 h-5 text-teal-600" />
                <h3 className="font-bold text-slate-900">Smart Actions</h3>
              </div>
              <TimelineIntegration
                tasks={mockTimeline.tasks}
                onTaskAction={handleTaskAction}
              />
            </div>

            {/* Week Overview */}
            <div className="bg-white rounded-xl border border-stone-200 p-6">
              <h3 className="font-bold text-slate-900 mb-4">This Week</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Planned Work</span>
                  <span className="text-sm font-medium text-slate-900">{mockTimeline.thisWeekHours}h</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Tasks Due</span>
                  <span className="text-sm font-medium text-slate-900">4</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Deadlines</span>
                  <span className="text-sm font-medium text-coral-600">2 urgent</span>
                </div>
              </div>
            </div>

            {/* Progress Ring */}
            <div className="bg-white rounded-xl border border-stone-200 p-6 text-center">
              <div className="relative inline-flex items-center justify-center w-24 h-24 mb-4">
                <svg className="w-24 h-24 transform -rotate-90">
                  <circle cx="48" cy="48" r="40" stroke="currentColor" strokeWidth="6" fill="none" className="text-stone-200" />
                  <circle cx="48" cy="48" r="40" stroke="currentColor" strokeWidth="6" fill="none" strokeDasharray={`${2 * Math.PI * 40}`} strokeDashoffset={`${2 * Math.PI * 40 * (1 - 0.65)}`} className="text-teal-500" strokeLinecap="round" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-lg font-bold text-slate-900">65%</span>
                  <span className="text-xs text-slate-600">Complete</span>
                </div>
              </div>
              <h3 className="font-medium text-slate-900 mb-1">Overall Progress</h3>
              <p className="text-sm text-slate-600">On track for deadlines</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CollegeJourneyHubMockup;